import dayjs from 'dayjs';
import { CustomerLocationComponent } from '../../../components/customer-profile/customer-location-component.js';
import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNotNullOrUndefinedOrEmpty } from '../../../utility/string.js';
import {
  cloneListOfCloneable,
  DataModel,
  DataModelBuilder,
  isSameData,
  isSameList,
  SerializationContext,
  SerializedData,
} from '../../dto/dto.js';
import { EventTicket } from '../event-ticket.js';
import { MonthOfYear } from '../month-of-year.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { Price } from '../price.js';
import { EventTicketOrder } from '../event-ticket-order.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { OrderSource } from '../order-source.js';
import { TicketType } from '../ticket-type.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { OrderDiscountDetails, StatisticsDiscountUtils } from '../../../utility/statistics_discount_utils.js';
import { LegacyPaymentHelper } from '../../../payment/legacy-payment-helper.js';

export enum OrderSummaryType {
  Offline = 'Offline',
  Online = 'Online',
  Promoter = 'Promoter',
  Protocol = 'Protocol',
 }

export class TicketSummary implements IDataComparable, ICloneable {
  constructor(
    public readonly ticketId: string,
    public readonly templateId: string,
    public readonly price: Price,
  ) {}

  static fromTicketOrder(order: EventTicketOrder, discountDetails: OrderDiscountDetails | null): TicketSummary[] {
    return order.tickets.map((e) => TicketSummary.fromEventTicket(e, discountDetails));
  }

  static fromOnlineTicketOrder(order: OnlineEventTicketOrder, discountDetails: OrderDiscountDetails | null): TicketSummary[] {
    const summaries = new Array<TicketSummary>();

    for(const ticket of order.tickets) {
      summaries.push(TicketSummary.fromEventTicket(ticket, discountDetails));
    }

    return summaries;
  }

  static fromEventTicket(ticket: EventTicket, discountDetails: OrderDiscountDetails | null): TicketSummary {
    return new TicketSummary(
        ticket.uniqueId,
        ticket.templateId,
        discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketSummary {
    return new TicketSummary(
        MapUtils.get<string>(map, 'ticketId'),
        MapUtils.get<string>(map, 'templateId'),
        Price.decode(MapUtils.get<string>(map, 'price')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'ticketId': this.ticketId,
      'templateId': this.templateId,
      'price': this.price.encode(),
    };
  }

  clone(): this {
    return new TicketSummary(
        this.ticketId,
        this.templateId,
        this.price.clone(),
    ) as this;
  }

  isSame(other: TicketSummary): boolean {
    if(other === this) {
      return true;
    }

    return other.ticketId === this.ticketId &&
      other.templateId === this.templateId &&
      isSameData(other.price, this.price);
  }
}

export class OrderSummary implements IDataComparable, ICloneable {
  constructor(
    public readonly orderId: string,
    public readonly date: dayjs.Dayjs,
    public readonly country: string | null,
    public readonly tickets: TicketSummary[],
    public readonly totalPrice: Price,
    public readonly isOnline: boolean | null,
    public readonly orderType: OrderSummaryType | null,
  ) {}

  static fromTicketOrder(order: EventTicketOrder): OrderSummary {
    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfTicketOrder(order);
    const tickets = TicketSummary.fromTicketOrder(order, discountDetails);
    const isOnline = order.source === OrderSource.Client;
    const isPromoter = order.tickets[0].ticketType === TicketType.Promoter;
    const isProtocol = order.tickets[0].ticketType === TicketType.Protocol;
    let orderType: OrderSummaryType;
    if(isPromoter) {
      orderType = OrderSummaryType.Promoter;
    }
    else if(isProtocol) {
      orderType = OrderSummaryType.Protocol;
    }
    else if(isOnline) {
      orderType = OrderSummaryType.Online;
    }
    else {
      orderType = OrderSummaryType.Offline;
    }

    return new OrderSummary(
        order.uniqueId,
        TimeZone.cloneDate(order.creationDate),
        OrderSummary.findTicketOrderCountry(order),
        tickets,
        // If selling price calculation fails, we use the legacy algorithm which calculates the sum of the ticket prices.
        discountDetails?.orderDetails?.discountedPrice ??
          LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true }) ??
          OrderSummary.calculateTotalPrice(tickets),
        isOnline,
        orderType,
    );
  }

  static fromOnlineTicketOrder(order: OnlineEventTicketOrder): OrderSummary {
    const discountDetails = StatisticsDiscountUtils.getOrderDiscountDetailsOfOnlineTicketOrder(order);
    const tickets = TicketSummary.fromOnlineTicketOrder(order, discountDetails);

    return new OrderSummary(
        order.uniqueId,
        TimeZone.cloneDate(order.creationDate),
        OrderSummary.findOnlineTicketOrderCountry(order),
        tickets,
        // If selling price calculation fails, we use the legacy algorithm which calculates the sum of the ticket prices.
        discountDetails?.orderDetails?.discountedPrice ??
          LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true }) ??
          OrderSummary.calculateTotalPrice(tickets),
        true,
        OrderSummaryType.Online,
    );
  }

  private static findTicketOrderCountry(order: EventTicketOrder): string | null {
    const customer = order.tryGetCustomer();
    const component = customer?.findComponentByTypeId<CustomerLocationComponent>(CustomerLocationComponent.TYPE_ID);
    const country = component?.location?.address?.country;

    if(isNotNullOrUndefinedOrEmpty(country)) {
      return country;
    }

    return null;
  }

  private static findOnlineTicketOrderCountry(order: OnlineEventTicketOrder): string | null {
    const customer = order.tryGetCustomer();
    const component = customer?.findComponentByTypeId<CustomerLocationComponent>(CustomerLocationComponent.TYPE_ID);
    const country = component?.location?.address?.country;

    if(isNotNullOrUndefinedOrEmpty(country)) {
      return country;
    }

    return null;
  }

  private static calculateTotalPrice(tickets: TicketSummary[]): Price {
    const currency = tickets[0].price.currency;
    let price = Price.zero(currency);

    for(const ticket of tickets) {
      if(ticket.price.currency === currency) {
        price = price.add(ticket.price);
      }
    }

    return price;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrderSummary {
    // TODO: Remove this in the future.
    let orderType: OrderSummaryType | null = toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'orderType'), OrderSummaryType);
    if(isNullOrUndefined(orderType)) {
      const isOnline = MapUtils.get<boolean>(map, 'isOnline');
      if(isNotNullOrUndefined(isOnline)) {
        orderType = isOnline ? OrderSummaryType.Online : OrderSummaryType.Offline;
      }
    }

    return new OrderSummary(
        MapUtils.get<string>(map, 'orderId'),
        context.decodeDate(MapUtils.get<number>(map, 'date')),
        MapUtils.getOrNull<string>(map, 'country'),
        MapUtils.getList(map, 'tickets', (data) => TicketSummary.fromMap(context, data as SerializedData)),
        Price.decode(MapUtils.get<string>(map, 'totalPrice')),
        MapUtils.getOrNull<boolean>(map, 'isOnline'),
        orderType,
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'orderId': this.orderId,
      'date': context.encodeDate(this.date),
      'country': this.country,
      'tickets': this.tickets.map((e) => e.toMap(context)),
      'totalPrice': this.totalPrice.encode(),
      'isOnline': this.isOnline,
      'orderType': fromEnumAsStringOrNull(this.orderType),
    };
  }

  clone(): this {
    return new OrderSummary(
        this.orderId,
        TimeZone.cloneDate(this.date),
        this.country ?? null,
        cloneListOfCloneable(this.tickets),
        this.totalPrice.clone(),
        this.isOnline,
        this.orderType,
    ) as this;
  }

  isSame(other: OrderSummary): boolean {
    if(other === this) {
      return true;
    }

    return other.orderId === this.orderId &&
      other.date === this.date &&
      other.country === this.country &&
      isSameList(other.tickets, this.tickets, isSameData) &&
      isSameData(other.totalPrice, this.totalPrice) &&
      other.isOnline === this.isOnline &&
      other.orderType === this.orderType;
  }
}

/**
 * @deprecated Use `MonthlySalesIncomeSummary` instead.
 */
export class MonthlySalesSummary extends DataModel {
  static readonly TYPE_ID: string = '77c641fc41c543e7a80e19fc9d58c876';

  constructor(
    public readonly monthOfYear: MonthOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly orders: OrderSummary[],
  ) {
    super();
  }

  get $typeId(): string {
    return MonthlySalesSummary.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): MonthlySalesSummary {
    return new MonthlySalesSummary(
        context.fromData<MonthOfYear>(MapUtils.get<SerializedData>(map, 'monthOfYear')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        MapUtils.getList<OrderSummary>(map, 'orders', (data) => OrderSummary.fromMap(context, data as SerializedData)),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'monthOfYear': context.toData(this.monthOfYear),
      'timezone': fromEnumAsString(this.timezone),
      'orders': this.orders.map((e) => e.toMap(context)),
    };
  }

  clone(): this {
    return new MonthlySalesSummary(
        this.monthOfYear.clone(),
        this.timezone,
        cloneListOfCloneable(this.orders),
    ) as this;
  }

  isSame(other: MonthlySalesSummary): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.monthOfYear, this.monthOfYear) &&
      other.timezone === this.timezone &&
      isSameList(other.orders, this.orders, isSameData);
  }
}

export class MonthlySalesSummaryBuilder extends DataModelBuilder<MonthlySalesSummary> {
  build(context: SerializationContext, map: SerializedData): MonthlySalesSummary {
    return MonthlySalesSummary.fromMap(context, map);
  }
}
